import {boundMethod} from "autobind-decorator";

import {EModuleLicenses, ESopLicenses} from "@/components/license/models";
import http from "@/services/http";
import license from "@/services/license";
import {ICellTypeModel} from "../sop/models";

class CellTypeService {
    private _cellTypes: ICellTypeModel[] = [];

    public get cellTypes() {
        let _cellTypes = this._cellTypes; // filter for non licensed cell-types

        if (!license.status.modules.includes(EModuleLicenses.Filtration)) {
            // do not include filt cells
            _cellTypes = _cellTypes.filter(
                (cell) => !this.onlyFiltCellTypeIds.includes(cell.id),
            );
        }

        if (!license.status.sops.includes(ESopLicenses.Magnet)) {
            // do not include magnetic cells
            _cellTypes = _cellTypes.filter(
                (cell) => !this.magneticCellTypeIds.includes(cell.id),
            );
        }

        if (!license.status.sops.includes(ESopLicenses.SectorShapedCells)) {
            // do not include sector cells
            _cellTypes = _cellTypes.filter(
                (cell) => !this.sscIds.includes(cell.id),
            );
        }

        return _cellTypes;
    }

    // those are bad on any psa analysis
    public get badPsaCellTypeIds() {
        return [10, 11, 14, 15, 16, 17, 18, 19, 20, 21, 28, 29, 50, 51];
    }

    // cell types that can filt analysis, but can non-filt as well, therefore no warning ever
    public get filtCellTypeIds() {
        return [50, 51, 52, 53, 54, 55, 56, 57, 58, 59];
    }

    // cell types only for filt analysis, show warning if choosen otherwise
    public get onlyFiltCellTypeIds() {
        return [110, 111];
    }

    // cell types for magnetic license
    public get magneticCellTypeIds() {
        return [
            90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104,
            105,
        ];
    }

    // those cannot be mixed with non SSC
    public get sscIds() {
        return [130, 131];
    }

    @boundMethod
    public getbyId(id: number | string) {
        return this._cellTypes.find((x) => x.id === id);
    }

    @boundMethod
    public getbyName(name: string) {
        return this._cellTypes.find((x) => x.name === name);
    }

    public getIdEverytime(id: number | string) {
        if (typeof id === "number") {
            return id;
        }

        return this.getbyName(id)?.id ?? 0;
    }

    // build in fallback, if backend sends name directly instead of id
    public getNamebyId(id: number | string) {
        if (typeof id === "string") {
            const cell = this.getbyName(id);

            return cell ? this.getName(cell) : id;
        }

        return this.getName(this.getbyId(id));
    }

    public getName(value?: ICellTypeModel) {
        if (!value) {
            return "-";
        }

        return `[${value.id}] ${value.name}`;
    }

    public async retrieve(bruteForce: boolean = false) {
        if (!bruteForce && this._cellTypes.length) {
            return;
        }

        try {
            const cells = await http
                .get("/api/centrifuge/sop/cells")
                .json<ICellTypeModel[]>();

            this._cellTypes = cells;
        } catch {
            this._cellTypes = [];
        }
    }
}

const cellTypes = new CellTypeService();
export default cellTypes;
